import { useCallback, useEffect, useState } from 'react';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, IconButton } from '@mui/material';
import { faCheck, faCheckCircle, faCompress, faExpand, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type ExpandableTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  onSubmit?: (text: string) => Promise<void>;
  disabled?: boolean;
};

export const ExpandableTextarea = ({ onSubmit, disabled, ...props }: ExpandableTextareaProps) => {
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState(props.value ?? '');
  const [lastSubmittedText, setLastSubmittedText] = useState(props.value ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasChanged = text.toString().trim() !== lastSubmittedText.toString().trim();

  const handleSubmit = useCallback(async () => {
    if (!hasChanged || isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(text.toString());
      }
      setLastSubmittedText(text.toString());
    } catch (err) {
      console.error('Submit failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [hasChanged, isSubmitting, onSubmit, text]);

  useEffect(() => {
    if (!expanded) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Tab') setExpanded(false)
      else if (e.key === 'Enter') {
        setExpanded(false)
        handleSubmit()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)

  }, [expanded, handleSubmit])

  return (
    <div className="relative">
      <TextareaAutosize
        {...props}
        value={props.value ?? text}
        onChange={(e) => {
          props.onChange?.(e);
          setText(e.target.value);
        }}
        minRows={1}
        maxRows={expanded ? 10 : 1}
        disabled={disabled || isSubmitting}
        className={`!w-full flex items-center bg-theme-surface-2 border border-theme-border rounded py-1 resize-none
          ${props.className ?? ''}
          ${expanded ? 'z-50 shadow-lg overflow-y-auto transition-height' : 'overflow-hidden'}`}
        style={{
          overflow: 'hidden',
          position: expanded ? 'absolute' : 'static',
          top: expanded ? -10 : 'auto',
          left: expanded ? 0 : 'auto',
          paddingLeft: '0.5rem',
          paddingRight: expanded ? '3.6rem' : '2.8rem'
        }}
        onFocus={() => setExpanded(true)}
      />

      <div className={`absolute ${expanded ? 'z-50 right-9 -translate-y-1' : 'z-[9] right-[24px] transition-right duration-150 top-2 -translate-y-1'}`}>
        <Button
          sx={{ minWidth: 0 }}
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          disabled={isSubmitting || disabled}
        >
          {expanded ? <FontAwesomeIcon icon={faCompress} /> : <FontAwesomeIcon icon={faExpand} />}
        </Button>
      </div>

      <div className={`absolute top-1 ${expanded ? 'z-50 right-3 -translate-y-3' : 'z-[9] right-[2px] transition-right duration-150 -translate-y-1'}`}>
        <IconButton
          size="small"
          disabled={!hasChanged || isSubmitting || disabled}
          onClick={handleSubmit}
          color={hasChanged && !isSubmitting ? 'primary' : 'default'}
          sx={{ marginBottom: hasChanged ? -1 : -1.5 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isSubmitting ? (
              <motion.span
                key="spinner"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <FontAwesomeIcon icon={faSpinner} spin />
              </motion.span>
            ) : (
              <motion.span
                key="icon"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {hasChanged ? (
                  <FontAwesomeIcon icon={faCheckCircle} className="animate-[pulse_.5s_ease-in-out] text-theme-success" />
                ) : (
                  <FontAwesomeIcon icon={faCheck} size="sm" className="text-theme-text-muted" />
                )}
              </motion.span>
            )}
          </AnimatePresence>
        </IconButton>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="fixed inset-0 bg-black/30 z-[41]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpanded(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
